import React, { Component } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Form,
} from "reactstrap";
import { APP_CONST, setStoredItem, resetStorage } from "@/store/dataStore";
import swal from "sweetalert2";

import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import authApi from "@/api/auth";
import keyholderApi from "@/api/keyholder";
import { handleError } from "@/util/errorHandler";
import properties from "@/api/properties";

class Login extends Component {
  state = {
    username: "",
    password: "",
    description: "",
    userType: "",
    isLoading: false,
  };

  componentDidMount() {
    resetStorage();
  }

  handleLogin = async event => {
    event.preventDefault();

    try {
      this.setState({ isLoading: true });

      const { username, password } = this.state;

      const token = await authApi.login({
        username,
        password,
        rememberMe: true,
      });

      const userData = await authApi.fetchUserData(username);
      const role = userData.userAccountDTO.authorities[0];

      const authorities = userData.userAccountDTO.authorities;
      const roles = userData.userAccountDTO.roles;

      const { bin, participantCode } = await properties.getBankInfo();

      setStoredItem(APP_CONST.SEC_TOKEN, token);
      setStoredItem(
        APP_CONST.USER_INFO,
        JSON.stringify({
          ...userData,
          participantBankName: bin,
          participantBankCode: participantCode,
        }),
      );
      setStoredItem(APP_CONST.USERNAME, this.state.username);
      setStoredItem(APP_CONST.USER_TYPE, role);
      setStoredItem(APP_CONST.USER_AUTHORITIES, JSON.stringify(authorities));
      setStoredItem(APP_CONST.USER_ROLES, JSON.stringify(roles));

      await this.redirectTo(role);
    } catch (err) {
      handleError(err, { title: err.detail });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleReinstallation = () => {
    setStoredItem(APP_CONST.CURRENT_ACTION, APP_CONST.ACTION_REINSTALL);
    setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_RE_INSTALL);
    this.props.history.push("/keyholder/re-install");
  };

  redirectTo = async role => {
    switch (role) {
      case APP_CONST.USER_TYPE_KEYHOLDER:
        return this.handleKeyholderAuth();

      case APP_CONST.USER_TYPE_ADMIN:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_ADMIN);
        return this.props.history.push("/waiting_for_approval");

      case APP_CONST.USER_TYPE_MANAGER:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_MANAGER);
        return this.props.history.push("/dashboard");

      case APP_CONST.USER_TYPE_AUDITOR:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_AUDITOR);
        return this.props.history.push("/auditor-users");

      case APP_CONST.USER_TYPE_AUTHORIZER:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_AUTHORIZER);
        return this.props.history.push("/recovery-requests");
      default:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_CUSTOM);
        return this.props.history.push("/blank-page");
    }
  };

  handleKeyholderAuth = async () => {
    const { status } = await keyholderApi.getStatusOfCurrentCase();

    switch (status) {
      default:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_REGISTRATION);

        return this.props.history.push("/keyholder/registration");

      case "RE_INSTALLATION":
        setStoredItem(APP_CONST.CURRENT_ACTION, APP_CONST.ACTION_REINSTALL);
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_RE_INSTALL);

        return this.props.history.push("/keyholder/re-install");

      case "NO_WRITE_PERMISSION":
        const error = new Error(
          "There is no write permission in the server file system. The key pair cannot be saved. Call an administrator to fix this issue",
        );

        return handleError(error, { title: "Key pair generation failed" });

      case "RESEND_SECRETS":
        const { value: isAccepted } = await swal({
          title: "Key pair generation failed",
          text: "Key pair hasn't generated. Try to generate it again?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Generate",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        });

        if (isAccepted) {
          await keyholderApi.createBankAccountRepeatRequest();
          await swal({
            title: "Key pair is generating",
            text: "Bank account is in the process of creation!",
            type: "success",
          });
        }
    }
  };

  handleChangeUsername = ({ target: { value: username } }) =>
    this.setState({ username });

  handleChangePassword = ({ target: { value: password } }) => {
    this.setState({ password });
  };

  getLoginForm = () => {
    return (
      <Col lg="6" md="8">
        <Card
          className="p-4"
          style={{
            borderColor: "#f2ecec",
            borderWidth: 10,
          }}>
          <CardBody>
            <h1>Login</h1>
            <Form onSubmit={this.handleLogin}>
              <InputGroup className="mb-3">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="icon-key" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="text"
                  autoFocus
                  placeholder="Username"
                  value={this.state.username}
                  onChange={this.handleChangeUsername}
                  data-cy="username"
                />
              </InputGroup>
              <InputGroup className="mb-4">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="icon-lock" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="password"
                  placeholder="Password"
                  value={this.state.password}
                  onChange={this.handleChangePassword}
                  data-cy="password"
                />
              </InputGroup>
              <Row className="justify-content-center">
                <Button
                  type="submit"
                  color="primary"
                  className={this.state.isLoading ? "" : "px-4"}
                  disabled={
                    this.state.isLoading ||
                    !this.state.username ||
                    !this.state.password
                  }
                  data-cy="submit">
                  <span>
                    Login
                    <LoadingButtonIcon loading={this.state.isLoading} />
                  </span>
                </Button>
              </Row>
            </Form>
          </CardBody>
        </Card>
        <p
          className="text-muted"
          style={{ marginTop: "16px", textAlign: "center" }}>
          Version {process.env.PACKAGE_VERSION}. Email:{" "}
          <a target="_top" href="mailto:bakong@nbc.org.kh">
            bakong@nbc.org.kh
          </a>
          <br />
          <a
            target="_blank"
            href="https://www.nbc.org.kh/download_files/bakong/Bakong_payment_gateway_user_manual.pdf"
            rel="noopener noreferrer">
            User manual
          </a>
        </p>
      </Col>
    );
  };

  render() {
    return (
      <div
        className="app flex-row align-items-center"
        style={{
          backgroundColor: "#333131",
        }}>
        <Container>
          <Row className="justify-content-center">{this.getLoginForm()}</Row>
        </Container>
      </div>
    );
  }
}

export default Login;
